import { useCallback, useMemo, useState } from "react";

interface FilterItem {
  filter: unknown;
  filterBy: string;
}

interface FilterChip {
  /** Unique identifier for the filter (typically the filterBy field) */
  key: string;
  /** Display label for the chip (formatted filter name) */
  label: string;
  /** Display value for the chip (formatted filter value) */
  value: string;
  /** Original filter value (can be string, array, or object) */
  rawValue: unknown;
  /** Callback to remove this specific filter */
  onRemove: () => void;
}

interface UseFilterStateOptions {
  initialFilters?: FilterItem[];
  onApply?: () => void;
  formatter?: (
    filterKey: string,
    filterValue: unknown
  ) => { label: string; value: string };
}

interface UseFilterStateReturn {
  filterVisible: boolean;
  filterValues: FilterItem[];
  appliedFilters: FilterItem[];
  filterChips: FilterChip[];
  activeFilterCount: number;

  setFilterValues: React.Dispatch<React.SetStateAction<FilterItem[]>>;
  changeSelectorValue: (filter: unknown, filterBy: string) => void;

  applyFilters: (filtersOverride?: FilterItem[]) => void;
  removeFilter: (index: number) => void;

  openFilter: () => void;
  closeFilter: () => void;
}

export function useFilterState(
  options: UseFilterStateOptions = {}
): UseFilterStateReturn {
  const { initialFilters = [], onApply, formatter } = options;

  const [filterVisible, setFilterVisible] = useState(false);
  const [filterValues, setFilterValues] =
    useState<FilterItem[]>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<FilterItem[]>(initialFilters);

  const changeSelectorValue = useCallback(
    (filter: unknown, filterBy: string) => {
      setFilterValues((prev) => {
        const existingIndex = prev.findIndex(
          (item) => item.filterBy === filterBy
        );

        if (existingIndex >= 0) {
          if (!filter || (Array.isArray(filter) && filter.length === 0)) {
            return prev.filter((item) => item.filterBy !== filterBy);
          }
          return prev.map((item, i) =>
            i === existingIndex ? { filter, filterBy } : item
          );
        }

        if (filter && (!Array.isArray(filter) || filter.length > 0)) {
          return [...prev, { filter, filterBy }];
        }

        return prev;
      });
    },
    []
  );

  const removeFilter = useCallback((indexValue: number) => {
    setAppliedFilters((prev) => {
      const newFilters = prev.filter((_, index) => index !== indexValue);
      setFilterValues(newFilters);
      return newFilters;
    });
  }, []);

  const applyFilters = useCallback(
    (filtersOverride?: FilterItem[]) => {
      const filtersToApply = filtersOverride ?? filterValues;
      if (filtersOverride) {
        setFilterValues(filtersOverride);
      }
      setFilterVisible(false);
      setAppliedFilters(filtersToApply);
      onApply?.();
    },
    [filterValues, onApply]
  );

  const openFilter = useCallback(() => setFilterVisible(true), []);
  const closeFilter = useCallback(() => setFilterVisible(false), []);

  const filterChips = useMemo((): FilterChip[] => {
    if (!appliedFilters || appliedFilters.length === 0) return [];

    return appliedFilters.map((item, index) => {
      // Default formatter logic for label
      const label = item.filterBy
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .toLowerCase();

      // Format the display value
      let value = "";
      if (Array.isArray(item.filter)) {
        value = item.filter.map((f) => String(f).replace(/_/g, " ")).join(", ");
      } else if (typeof item.filter === "object" && item.filter !== null) {
        const filterObj = item.filter as { ids?: string[] };
        value = (filterObj.ids || []).join(", ");
      } else {
        value = String(item.filter).replace(/_/g, " ");
      }

      // Apply custom formatter if provided (for label/value override)
      const formatted = formatter
        ? formatter(item.filterBy, item.filter)
        : { label, value };

      return {
        key: item.filterBy,
        label: formatted.label,
        value: formatted.value,
        rawValue: item.filter,
        onRemove: () => removeFilter(index),
      };
    });
  }, [appliedFilters, formatter, removeFilter]);

  const activeFilterCount = appliedFilters.length;

  return {
    filterVisible,
    filterValues,
    appliedFilters,
    filterChips,
    activeFilterCount,
    setFilterValues,
    changeSelectorValue,
    applyFilters,
    removeFilter,
    openFilter,
    closeFilter,
  };
}

export type {
  FilterItem,
  FilterChip,
  UseFilterStateOptions,
  UseFilterStateReturn,
};
